import type { BrandStyle, Moodboard, MoodboardMember, MoodboardProfile } from '~/design-system/components/DeckGen/types/presentation'
import type { BrandStyleTokens } from '~/design-system/components/DeckGen/ai/deckFlowPrompt'
import { styleStrengthTier } from '~/design-system/components/DeckGen/ai/deckFlowPrompt'
import type { GenerationModel } from '~/design-system/components/DeckGen/ai/deckGenSchema'

/**
 * How a moodboard feeds generation: its guideline becomes a prompt block, its palette/roles/fonts
 * become brand-style tokens (the SAME `## Brand style` plumbing the website-brand path uses), and its
 * member images go along as visual style references (the same `imageMode:'style'` plumbing the
 * reference-image path uses). Shared by the node-flow "generate from inputs" path and (later) the
 * full-deck generate modal.
 */

/** The moodboard's profile as a prompt block appended to the generation seed. Palette/roles/fonts are
 *  NOT repeated here — they ride the dedicated `## Brand style` block (moodboardToBrandTokens). */
export function buildMoodboardPromptBlock(board: Moodboard, strength?: number | null): string {
  const profile = board.profile
  if (!profile) return ''
  const tier = styleStrengthTier(strength)
  if (tier === 'off') return ''
  // The strength tier only changes how strictly the direction is framed — the guideline/keywords/avoids
  // themselves are unchanged.
  const direction = {
    strict: 'Style direction (follow strictly):',
    close: 'Style direction (follow closely; prioritize legibility where they conflict):',
    light: 'Style direction (light inspiration only — do not copy; deviate freely for content fit):',
    faint: 'Style direction (a faint nod only — prioritize clean, legible default design):',
    off: '',
  }[tier]
  const lines = [
    `## Mood board: ${board.title}`,
    '',
    direction,
    profile.guideline,
  ]
  // Concrete spec rows (synthesized alongside the prose guideline) — these make adoption precise:
  // type hierarchy, spacing rhythm, and elevation. Tone steers the COPY, not just the look.
  if (profile.typography) lines.push('', `Type system: ${profile.typography}`)
  if (profile.spacing) lines.push('', `Spacing: ${profile.spacing}`)
  if (profile.shadows) lines.push('', `Shadows: ${profile.shadows}`)
  if (profile.tone) lines.push('', `Tone of voice (apply to ALL copy you write): ${profile.tone}`)
  // Usage RULES — how the system is actually used (the consistency reduce's output). These are what let a
  // NEW slide reuse the system instead of copying one: which colour does what, the layout moves, the chrome.
  if (profile.patterns?.length) {
    lines.push('', 'How this system is used (follow these rules):', ...profile.patterns.map((p) => `- ${p}`))
  }
  if (profile.keywords.length > 0) lines.push('', `Style keywords: ${profile.keywords.join(', ')}`)
  if (profile.avoids.length > 0) lines.push('', `Never use: ${profile.avoids.join(', ')}`)
  return lines.join('\n')
}

/** The moodboard's exact tokens for the `## Brand style` generation block — palette + semantic roles +
 *  fonts, the same shape the website-brand path passes (this is what makes adoption precise).
 *  When the board references a website whose CSS tokens are available (`exact`), those win over the
 *  profile's visual estimates — pixels can't name fonts or read exact hexes; CSS can. */
export function moodboardToBrandTokens(board: Moodboard, exact?: BrandStyle | null): BrandStyleTokens | undefined {
  const profile = board.profile
  if (!profile && !exact) return undefined
  const palette = exact?.palette?.length ? exact.palette : profile?.palette ?? []
  const roles = exact?.roles && Object.keys(exact.roles).length > 0 ? exact.roles : profile?.roles
  const headingFont = exact?.headingFont || profile?.headingFont
  const bodyFont = exact?.bodyFont || profile?.bodyFont
  const hasAny = palette.length > 0 || Object.keys(roles ?? {}).length > 0 || headingFont || bodyFont
  if (!hasAny) return undefined
  return {
    palette,
    roles,
    headingFont,
    bodyFont,
    typography: exact?.typography,
    headingFontWeights: headingFaceWeights(exact),
    logoUrl: exact?.logoUrl,
  }
}

/** Synthesize ONE image into a style profile — the single-image form of moodboard synthesis (same
 *  `/deckgen-moodboard-api` vision pass). Used when an image is wired into a slide as a STYLE reference:
 *  rasterize → synthesize → cache the profile on the connection. Returns null on any failure. */
export async function synthesizeImageStyle(src: string): Promise<MoodboardProfile | null> {
  const dataUrl = await rasterizeImageSource(src, 768)
  if (!dataUrl) return null
  try {
    const res = await fetch('/deckgen-moodboard-api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ images: [dataUrl] }),
    })
    if (!res.ok) return null
    const result = (await res.json()) as MoodboardProfile & { title?: string }
    const { title: _title, ...profile } = result
    return profile
  } catch {
    return null
  }
}

/** Synthesize a full moodboard profile from REFERENCE IMAGES (the Generate-modal / Birtvy wired path —
 *  no on-canvas Moodboard entity involved). This is the old canvas's rich style read (guideline, palette
 *  roles, fonts, tone, keywords/avoids) that the modal path never had. Same fail-safe contract as
 *  synthesizeImageStyle: null on ANY failure so generation proceeds with the raw images alone. `model`
 *  should be the user's picked generation model when it can see images (per Guri: reference processing
 *  follows the picked model); the route falls back to its own default otherwise. */
export async function synthesizeReferenceStyle(
  images: string[],
  model?: GenerationModel,
): Promise<(MoodboardProfile & { title?: string }) | null> {
  if (!images.length) return null
  try {
    const res = await fetch('/deckgen-moodboard-api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ images: images.slice(0, 12), model }),
      signal: AbortSignal.timeout(60_000),
    })
    if (!res.ok) return null
    return (await res.json()) as MoodboardProfile & { title?: string }
  } catch {
    return null
  }
}

/** Wrap a bare synthesized profile as a Moodboard so buildMoodboardPromptBlock / moodboardToBrandTokens
 *  can consume it — the modal/wired reference path has no board entity on the canvas. */
export function profileAsBoard(profile: MoodboardProfile & { title?: string }): Moodboard {
  return {
    id: 'reference-style',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    title: profile.title || 'Reference style',
    members: [],
    status: 'ready',
    profile,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }
}

/** The weights the heading font's @font-face files actually ship — generation must not request others
 *  (the browser would synthesize fake bold, crushing single-weight display faces). */
function headingFaceWeights(style: BrandStyle | null | undefined): string[] | undefined {
  const family = style?.headingFont?.trim().toLowerCase()
  if (!family || !style?.fontFaces?.length) return undefined
  const weights = [
    ...new Set(
      style.fontFaces
        .filter((face) => face.family.trim().toLowerCase() === family)
        .map((face) => (face.weight === 'bold' ? '700' : !face.weight || face.weight === 'normal' ? '400' : face.weight)),
    ),
  ]
  return weights.length > 0 ? weights.sort((a, b) => Number(a) - Number(b)) : undefined
}

/** A website's CSS-extracted brand style as generation tokens — for the node-wiring path where a web
 *  card is wired into a slide WITHOUT a moodboard (the node form of "From your website brand"). */
export function brandStyleToTokens(style: BrandStyle | null | undefined): BrandStyleTokens | undefined {
  if (!style) return undefined
  const hasAny =
    (style.palette?.length ?? 0) > 0 || Object.keys(style.roles ?? {}).length > 0 || style.headingFont || style.bodyFont
  if (!hasAny) return undefined
  return {
    palette: style.palette,
    roles: style.roles,
    headingFont: style.headingFont,
    bodyFont: style.bodyFont,
    typography: style.typography,
    headingFontWeights: headingFaceWeights(style),
    logoUrl: style.logoUrl,
  }
}

/** Raw inline SVG markup → a data URL an <img> can load. */
function svgMarkupToDataUrl(markup: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
}

/** Normalize a member source for storage/rendering: inline SVG markup becomes a data URL; everything
 *  else passes through. Used when COLLECTING members so cards/thumbnails/synthesis all get a loadable src. */
export function normalizeMemberSrc(src: string): string {
  return src.trimStart().startsWith('<svg') ? svgMarkupToDataUrl(src) : src
}

/** Load a source into an HTMLImageElement (crossOrigin-tagged so canvas readback isn't tainted when
 *  the host sends CORS headers — most CDNs do). */
function loadImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    if (crossOrigin) img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load image'))
    img.src = src
  })
}

/** Draw an image to a canvas at ≤ maxDim and export a JPEG dataURL. Rasterizes any browser-renderable
 *  source (including SVG) into a vision-model-safe format. White underlay so transparency doesn't go black. */
function drawToJpegDataUrl(img: HTMLImageElement, maxDim: number): string | null {
  const width = img.naturalWidth || img.width || 800
  const height = img.naturalHeight || img.height || 600
  if (!width || !height) return null
  const scale = Math.min(1, maxDim / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  try {
    return canvas.toDataURL('image/jpeg', 0.9)
  } catch {
    // Tainted canvas (cross-origin without CORS approval) — caller falls back to another strategy.
    return null
  }
}

/**
 * Rasterize ANY member image source into a vision-sized JPEG dataURL, trying every strategy in order:
 *  1. inline SVG markup → data URL → canvas;
 *  2. data:/blob: sources → direct <img> → canvas;
 *  3. remote URLs: direct fetch (same-origin / CORS-open hosts) → blob → canvas;
 *  4. the same-origin image proxy (hosts without CORS headers);
 *  5. last resort: CORS-tagged <img> straight from the URL → canvas (works when the host sends
 *     ACAO:* but rejects our proxy, e.g. hotlink-protected CDNs that allow browser image loads).
 * Returns null only when every strategy fails — the member is skipped, never the whole board.
 */
export async function rasterizeImageSource(src: string, maxDim = 768): Promise<string | null> {
  const normalized = normalizeMemberSrc(src)
  const isRemote = normalized.startsWith('http://') || normalized.startsWith('https://')

  // Local sources (data:/blob:/svg-markup) render directly.
  if (!isRemote) {
    try {
      return drawToJpegDataUrl(await loadImage(normalized, false), maxDim)
    } catch {
      return null
    }
  }

  // Remote: direct fetch → proxy fetch → CORS-tagged <img>.
  for (const fetchUrl of [normalized, `/deckgen-image-proxy-api?url=${encodeURIComponent(normalized)}`]) {
    try {
      const res = await fetch(fetchUrl, fetchUrl.startsWith('/') ? { credentials: 'include' } : undefined)
      if (!res.ok) continue
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      try {
        const result = drawToJpegDataUrl(await loadImage(objectUrl, false), maxDim)
        if (result) return result
      } finally {
        URL.revokeObjectURL(objectUrl)
      }
    } catch {
      // try the next strategy
    }
  }
  try {
    return drawToJpegDataUrl(await loadImage(normalized, true), maxDim)
  } catch {
    return null
  }
}

/** Rasterize the board's member images to vision-ref dataURLs (style references for generation),
 *  mirroring the website-brand path's signal quality:
 *  - web members FIRST — a full-page screenshot is a complete style system (typography, palette, mood)
 *    and rides at the same high resolution the website-brand path uses, so the model can READ the type;
 *  - remaining slots are filled with images sampled EVENLY across the board, not just the first few
 *    (which biases the style toward whatever happened to be selected first).
 *  Members that fail to load are skipped. */
export async function memberImagesToDataUrls(members: MoodboardMember[], cap = 4): Promise<string[]> {
  const webs = members.filter((m) => m.kind === 'web').slice(0, cap)
  const images = members.filter((m) => m.kind !== 'web')
  const slots = Math.min(cap - webs.length, images.length)
  const sampled: MoodboardMember[] = []
  for (let i = 0; i < slots; i++) sampled.push(images[Math.floor((i * images.length) / slots)])
  const picked = [...webs, ...sampled]
  const results = await Promise.all(
    picked.map((member) => rasterizeImageSource(member.src, member.kind === 'web' ? 1568 : 1024)),
  )
  return results.filter((src): src is string => src !== null)
}
